/**
 * /solucoes/:segment — páginas de segmento (landing pages de captação).
 *
 * A landing responde à busca de MARCA e à genérica ("tour virtual 360").
 * Quem está pronto para comprar busca com a própria dor: "tour virtual
 * para imobiliária", "decorado virtual construtora", "tour 360 hotel".
 * Cada segmento aqui é uma URL indexável respondendo a essa busca, com o
 * conteúdo, a FAQ e o CTA daquele público.
 *
 * SEO da página: H1 único, hierarquia H2/H3 real, FAQPage + BreadcrumbList
 * em JSON-LD (rich result no Google), canônica própria, links internos
 * entre segmentos e para /pricing. O conteúdo é i18n puro (4 idiomas).
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { ArrowRight, Check, ChevronRight } from 'lucide-react';
import { useSeo } from '@/lib/seo';
import { useJsonLd } from '@/lib/json-ld';
import { Button } from '@/components/ui/button';
import { LogoIcon } from '@/components/ui/Logo';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { useAuthStore } from '@/store/authStore';

/** Slugs válidos — a ordem define a navegação "outros segmentos". */
export const SOLUTION_SLUGS = [
  'imobiliarias',
  'construtoras',
  'hoteis',
  'lojas',
  'eventos',
  'museus',
] as const;
export type SolutionSlug = (typeof SOLUTION_SLUGS)[number];

/** Nome de cada segmento — chave LITERAL, exigida pelos tipos de i18n
 *  gerados no build. Exportado porque o rodapé da landing também lista. */
export const SOLUTION_NAME_KEY = {
  imobiliarias: 'solutions:items.imobiliarias.name',
  construtoras: 'solutions:items.construtoras.name',
  hoteis: 'solutions:items.hoteis.name',
  lojas: 'solutions:items.lojas.name',
  eventos: 'solutions:items.eventos.name',
  museus: 'solutions:items.museus.name',
} as const;

interface Benefit {
  title: string;
  text: string;
}
interface Faq {
  q: string;
  a: string;
}

/**
 * Chaves ENUMERADAS, não montadas por template.
 *
 * O repo gera tipos em tempo de compilação para cada chave i18n; uma chave
 * dinâmica (`solutions:items.${slug}.h1`) escapa dessa checagem e deixa o
 * texto quebrar em produção sem ninguém notar. Enumerar custa um bloco e
 * devolve a garantia: chave inexistente vira erro de build.
 */
const K = {
  imobiliarias: {
    name: 'solutions:items.imobiliarias.name',
    seoTitle: 'solutions:items.imobiliarias.seoTitle',
    seoDescription: 'solutions:items.imobiliarias.seoDescription',
    h1: 'solutions:items.imobiliarias.h1',
    lead: 'solutions:items.imobiliarias.lead',
    benefits: 'solutions:items.imobiliarias.benefits',
    faq: 'solutions:items.imobiliarias.faq',
  },
  construtoras: {
    name: 'solutions:items.construtoras.name',
    seoTitle: 'solutions:items.construtoras.seoTitle',
    seoDescription: 'solutions:items.construtoras.seoDescription',
    h1: 'solutions:items.construtoras.h1',
    lead: 'solutions:items.construtoras.lead',
    benefits: 'solutions:items.construtoras.benefits',
    faq: 'solutions:items.construtoras.faq',
  },
  hoteis: {
    name: 'solutions:items.hoteis.name',
    seoTitle: 'solutions:items.hoteis.seoTitle',
    seoDescription: 'solutions:items.hoteis.seoDescription',
    h1: 'solutions:items.hoteis.h1',
    lead: 'solutions:items.hoteis.lead',
    benefits: 'solutions:items.hoteis.benefits',
    faq: 'solutions:items.hoteis.faq',
  },
  lojas: {
    name: 'solutions:items.lojas.name',
    seoTitle: 'solutions:items.lojas.seoTitle',
    seoDescription: 'solutions:items.lojas.seoDescription',
    h1: 'solutions:items.lojas.h1',
    lead: 'solutions:items.lojas.lead',
    benefits: 'solutions:items.lojas.benefits',
    faq: 'solutions:items.lojas.faq',
  },
  eventos: {
    name: 'solutions:items.eventos.name',
    seoTitle: 'solutions:items.eventos.seoTitle',
    seoDescription: 'solutions:items.eventos.seoDescription',
    h1: 'solutions:items.eventos.h1',
    lead: 'solutions:items.eventos.lead',
    benefits: 'solutions:items.eventos.benefits',
    faq: 'solutions:items.eventos.faq',
  },
  museus: {
    name: 'solutions:items.museus.name',
    seoTitle: 'solutions:items.museus.seoTitle',
    seoDescription: 'solutions:items.museus.seoDescription',
    h1: 'solutions:items.museus.h1',
    lead: 'solutions:items.museus.lead',
    benefits: 'solutions:items.museus.benefits',
    faq: 'solutions:items.museus.faq',
  },
} as const;

export default function Solution() {
  const { segment } = useParams({ strict: false }) as { segment?: string };
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAuthenticated } = useAuthStore();

  const slug = (SOLUTION_SLUGS as readonly string[]).includes(segment ?? '')
    ? (segment as SolutionSlug)
    : 'imobiliarias';

  const k = K[slug];
  // returnObjects devolve o array do JSON; o tipo gerado descreve só
  // strings, daí o cast explícito por unknown.
  const benefits = t(k.benefits, { returnObjects: true }) as unknown as Benefit[];
  const faqs = t(k.faq, { returnObjects: true }) as unknown as Faq[];
  const path = `/solucoes/${slug}`;

  useSeo({
    title: t(k.seoTitle),
    description: t(k.seoDescription),
    canonicalPath: path,
  });

  // Rich results: a FAQ pode aparecer expandida no resultado do Google, e
  // o breadcrumb substitui a URL crua por uma trilha legível.
  const jsonLd = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'FAQPage',
          '@id': `https://my360.site${path}#faq`,
          mainEntity: (Array.isArray(faqs) ? faqs : []).map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        },
        {
          '@type': 'BreadcrumbList',
          '@id': `https://my360.site${path}#breadcrumb`,
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: t('solutions:breadcrumbHome'), item: 'https://my360.site/' },
            { '@type': 'ListItem', position: 2, name: t(k.name), item: `https://my360.site${path}` },
          ],
        },
      ],
    }),
    [faqs, path, k, t],
  );
  useJsonLd(`solution-${slug}`, jsonLd);

  const others = SOLUTION_SLUGS.filter((s) => s !== slug);

  return (
    <div className="min-h-screen bg-[var(--color-base)] text-[var(--color-text)] overflow-y-auto">
      {/* ── Top nav ── */}
      <nav className="fixed top-0 inset-x-0 z-50 px-6 py-4 flex items-center justify-between glass-panel border-x-0 border-t-0">
        <button onClick={() => navigate({ to: '/' })} className="flex items-center gap-1.5">
          <LogoIcon size="lg" />
          {/* eslint-disable-next-line i18next/no-literal-string */}
          <span className="font-logo text-2xl leading-none">my360</span>
        </button>
        <div className="flex items-center gap-3">
          <ThemeToggle />
          <Button variant="ghost" onClick={() => navigate({ to: '/pricing' })}>
            {t('solutions:ctaSecondary')}
          </Button>
          <Button
            variant="primary"
            onClick={() => navigate({ to: isAuthenticated ? '/dashboard' : '/register' })}
          >
            {t('solutions:ctaPrimary')}
          </Button>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="relative pt-32 pb-16 overflow-hidden border-b border-[var(--color-border)]">
        <div className="absolute inset-0 bg-gradient-to-b from-[var(--color-primary)]/10 to-transparent pointer-events-none" />
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-[var(--color-primary)]/20 blur-[140px] opacity-50 pointer-events-none" />
        <div className="relative z-10 max-w-3xl mx-auto px-6">
          {/* Trilha visível — espelha o BreadcrumbList do JSON-LD. */}
          <nav aria-label={t('solutions:breadcrumbSolutions')} className="mb-6 flex items-center gap-1.5 text-xs text-[var(--color-text-muted)]">
            <button onClick={() => navigate({ to: '/' })} className="hover:text-[var(--color-text)] transition-colors">
              {t('solutions:breadcrumbHome')}
            </button>
            <ChevronRight className="h-3 w-3" />
            <span className="text-[var(--color-text)]">{t(k.name)}</span>
          </nav>
          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="text-display text-4xl md:text-5xl font-semibold tracking-tighter mb-5 leading-[1.08]"
          >
            {t(k.h1)}
          </motion.h1>
          <p className="text-lg md:text-xl text-[var(--color-text-muted)] font-light">
            {t(k.lead)}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button
              variant="primary"
              size="lg"
              onClick={() => navigate({ to: isAuthenticated ? '/dashboard' : '/register' })}
            >
              {t('solutions:ctaPrimary')}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button variant="outline" size="lg" onClick={() => navigate({ to: '/pricing' })}>
              {t('solutions:ctaSecondary')}
            </Button>
          </div>
          <p className="mt-4 text-xs text-[var(--color-text-muted)]">{t('solutions:trustLine')}</p>
        </div>
      </section>

      {/* ── Benefícios ── */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <h2 className="text-display text-3xl font-semibold tracking-tight mb-10">
          {t('solutions:benefitsTitle')}
        </h2>
        <div className="grid gap-6 md:grid-cols-2">
          {(Array.isArray(benefits) ? benefits : []).map((b) => (
            <div
              key={b.title}
              className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-6"
            >
              <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-xl bg-[var(--color-primary)]/12">
                <Check className="h-4 w-4 text-[var(--color-primary)]" />
              </div>
              <h3 className="mb-2 text-lg font-semibold">{b.title}</h3>
              <p className="text-sm leading-relaxed text-[var(--color-text-muted)]">{b.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── FAQ (espelha o FAQPage do JSON-LD) ── */}
      <section className="max-w-3xl mx-auto px-6 pb-16">
        <h2 className="text-display text-3xl font-semibold tracking-tight mb-8">
          {t('solutions:faqTitle')}
        </h2>
        <div className="space-y-3">
          {(Array.isArray(faqs) ? faqs : []).map((f) => (
            <details
              key={f.q}
              className="group rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5"
            >
              <summary className="cursor-pointer list-none font-semibold marker:hidden flex items-center justify-between gap-4">
                {f.q}
                <ChevronRight className="h-4 w-4 shrink-0 text-[var(--color-text-muted)] transition-transform group-open:rotate-90" />
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-[var(--color-text-muted)]">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* ── Links internos: o Google descobre os outros segmentos daqui ── */}
      <section className="max-w-5xl mx-auto px-6 pb-24">
        <h2 className="text-display text-2xl font-semibold tracking-tight mb-6">
          {t('solutions:otherTitle')}
        </h2>
        <div className="flex flex-wrap gap-2">
          {others.map((s) => (
            <a
              key={s}
              href={`/solucoes/${s}`}
              onClick={(e) => {
                e.preventDefault();
                navigate({ to: '/solucoes/$segment', params: { segment: s } });
              }}
              className="rounded-full border border-[var(--color-border)] px-4 py-2 text-sm text-[var(--color-text-muted)] transition-colors hover:border-[var(--color-primary)]/40 hover:text-[var(--color-text)]"
            >
              {t(SOLUTION_NAME_KEY[s])}
            </a>
          ))}
        </div>
      </section>
    </div>
  );
}
